import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Upload, Settings, BarChart3 } from "lucide-react";
import { useAuth } from "../hooks/useAuth";


export const Landing = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  // Smooth scroll if navigating with #pricing in URL
  useEffect(() => {
    if (location.hash === "#pricing") {
      const section = document.getElementById("pricing");
      if (section) {
        setTimeout(() => {
          section.scrollIntoView({ behavior: "smooth" });
        }, 200);
      }
    }
  }, [location]);

  // ⛔ Removed the forced redirect: keep the landing page visible even if logged in.

  return (
    <>


      {/* Hero Section */}
      <section className="hero">
        <div className="container hero-content">
          <h1>
            Missouri Campaign Finance{" "}
            <span className="accent">Transparency</span>
          </h1>
          <p>
            Transform Missouri campaign finance PDFs into clean spreadsheets in seconds. Upload MEC reports and download ready-to-analyze CSV or Excel files — no manual data entry required.
          </p>
          <div className="actions">
            <Link to="/signup" className="btn-primary">
              Get Started Free
            </Link>
            <Link to="/#pricing" className="btn-outline">
              See Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how">
        <div className="container">
          <h2>How It Works</h2>
          <div className="features">
            <div className="feature-card">
              <div className="icon"><Upload size={40} /></div>
              <h3>Upload PDF Reports</h3>
              <p>Drag and drop MEC CD1 or CD3 filings into our secure portal.</p>
            </div>
            <div className="feature-card">
              <div className="icon"><Settings size={40} /></div>
              <h3>Automatic Extraction</h3>
              <p>Our engine parses donor and expense data with 95%+ accuracy.</p>
            </div>
            <div className="feature-card">
              <div className="icon"><BarChart3 size={40} /></div>
              <h3>Download Results</h3>
              <p>Receive clean CSV or Excel files ready for instant analysis.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing">
        <div className="container">
          <h2>Simple Annual Pricing</h2>
          <p style={{ textAlign: "center", color: "#666", marginBottom: "2.5rem" }}>
            No monthly commitments, no credits, no math. Pick a plan, use it all year.
          </p>

          {/* Subscription Tiers */}
          <div className="pricing-grid">
            {/* Free Tier */}
            <div className="price-card free">
              <h3>Free</h3>
              <p className="subtitle">Try it out.</p>
              <p className="price">$0</p>
              <ul className="feature-list">
                <li>3 free report uploads</li>
                <li>Automated data extraction</li>
                <li>Download CSV/XLSX results</li>
                <li>Access to reports you upload</li>
              </ul>
              <Link to="/signup" className="btn-primary small">
                Start Free
              </Link>
            </div>

            {/* Basic Tier */}
            <div className="price-card basic">
              <h3>Basic</h3>
              <p className="subtitle">For regular users.</p>
              <p className="price">
                $10<span>/year</span>
              </p>
              <ul className="feature-list">
                <li>Up to 100 report uploads per year</li>
                <li>Unlimited viewing of reports you upload</li>
                <li>Automated data extraction</li>
                <li>Download CSV/XLSX results</li>
                <li>Email support</li>
              </ul>
              <Link to="/signup" className="btn-primary small">
                Upgrade to Basic
              </Link>
            </div>

            {/* Max Tier (internal key: pro) */}
            <div className="price-card pro">
              <div className="popular-badge">Recommended</div>
              <h3>Max</h3>
              <p className="subtitle">For power users.</p>
              <p className="price">
                $20<span>/year</span>
              </p>
              <ul className="feature-list">
                <li>Unlimited report uploads</li>
                <li>Access to every report in the system</li>
                <li>Full analytics dashboard</li>
                <li><strong>Reporting Entity Groups (committees + candidates)</strong></li>
                <li>Priority email support</li>
              </ul>
              <Link to="/signup" className="btn-primary small">
                Upgrade to Max
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <h2>Start Analyzing Campaign Finance Data Today</h2>
          <p>Sign up for free and see how easy transparency can be.</p>
          <Link to="/signup" className="btn-primary">
            Get Started Free
          </Link>
        </div>
      </section>


    </>
  );
};
